import { useEffect, useMemo, useState } from "react";
import { Snackbar } from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import { useNavigate } from "react-router-dom";
import UtilsFormule from "../../utils/UtilsFormule";
import ManufacturingBatchService from "../../services/ManufacturingBatchService";
import getCurrentUserId from "./../../reduxSaga/sagas/requests/currentUserId";

const PAGE_SIZE = 10;
const EXPIRY_WARNING_DAYS = 30;

const labelStyle = {
  fontSize: "0.8rem",
  fontWeight: 600,
  textTransform: "uppercase",
  letterSpacing: "0.03em",
  color: "#6c757d",
};
const cardStyle = { borderRadius: 8, boxShadow: "0 1px 6px rgba(0,0,0,.08)" };

// pastille de date : rouge si dépassée, orange si proche
const ExpiryDate = ({ value }) => {
  if (!value) return <span className="text-muted">—</span>;
  const days = Math.ceil((new Date(value).getTime() - Date.now()) / 86400000);
  let badge = null;
  if (days < 0) {
    badge = <span className="badge badge-danger ml-2">Expiré</span>;
  } else if (days <= EXPIRY_WARNING_DAYS) {
    badge = <span className="badge badge-warning ml-2">{days} j</span>;
  }
  return (
    <span>
      {UtilsFormule.formatDate(value)}
      {badge}
    </span>
  );
};

const ManufacturingBatch = () => {
  const navigate = useNavigate();

  const [availableLot, setAvailableLot] = useState([]);
  const [archivedLot, setArchivedLot] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("dispo"); // dispo | archived
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [alert, setAlert] = useState({ open: false, text: "", severity: "info" });

  const loadLots = () => {
    setLoading(true);
    const idUser = getCurrentUserId();
    Promise.all([
      ManufacturingBatchService.getManufacturingBatchByIdUser(idUser, 0),
      ManufacturingBatchService.getManufacturingBatchByIdUser(idUser, 1),
    ])
      .then(([available, archived]) => {
        setAvailableLot(Array.isArray(available.data) ? available.data : []);
        setArchivedLot(Array.isArray(archived.data) ? archived.data : []);
      })
      .catch((e) => {
        console.log(e);
        setAlert({ open: true, text: "Erreur : chargement des lots impossible", severity: "error" });
      })
      .finally(() => setLoading(false));
  };

  useEffect(loadLots, []);
  useEffect(() => setPage(0), [status, search]);

  const isArchivedTab = status === "archived";
  const filteredLots = useMemo(() => {
    const term = search.trim().toLowerCase();
    const lots = isArchivedTab ? archivedLot : availableLot;
    return term
      ? lots.filter((l) => String(l.serialNumber || "").toLowerCase().includes(term))
      : lots;
  }, [availableLot, archivedLot, isArchivedTab, search]);

  const pageCount = Math.max(1, Math.ceil(filteredLots.length / PAGE_SIZE));
  const pageLots = filteredLots.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
  const expiredCount = availableLot.filter(
    (l) => l.dlc && new Date(l.dlc).getTime() < Date.now()
  ).length;

  const onArchiveHandler = (id, deleted) => {
    ManufacturingBatchService.deleteManufacturingBatchById(getCurrentUserId(), id, deleted)
      .then((response) => {
        if (response.data["success"]) {
          setAlert({
            open: true,
            text: deleted === 1 ? "Lot archivé avec succès" : "Lot restauré avec succès",
            severity: "info",
          });
          loadLots();
        }
      })
      .catch((e) => console.log(e));
  };

  const statusBtnClass = (value) =>
    `btn btn-sm ${status === value ? "btn-dark" : "btn-outline-dark"}`;

  const closeAlert = (event, reason) => {
    if (reason === "clickaway") return;
    setAlert((a) => ({ ...a, open: false }));
  };

  return (
    <div className="content-wrapper" style={{ marginTop: "0" }}>
      <section className="content-header">
        <div className="container-fluid">
          <div className="d-flex flex-wrap justify-content-between align-items-center">
            <div>
              <h4 className="mb-1">
                <i className="fas fa-layer-group mr-2"></i>Lots
              </h4>
              <small className="text-muted">
                Suivez vos lots de fabrication et leurs dates limites
              </small>
            </div>
            <div className="mt-2 mt-sm-0">
              <button className="btn btn-dark" onClick={() => navigate(`/addLot`)}>
                <i className="fas fa-plus mr-1"></i>
                Ajouter un lot
              </button>
            </div>
          </div>
        </div>
      </section>

      <div className="content">
        <div className="container-fluid">
          <div className="row">
            <div className="col-md-4 col-sm-6">
              <div className="info-box" style={cardStyle}>
                <span className="info-box-icon bg-success">
                  <i className="fas fa-check-circle"></i>
                </span>
                <div className="info-box-content">
                  <span className="info-box-text">Lots disponibles</span>
                  <span className="info-box-number">{availableLot.length}</span>
                </div>
              </div>
            </div>
            <div className="col-md-4 col-sm-6">
              <div className="info-box" style={cardStyle}>
                <span className="info-box-icon bg-danger">
                  <i className="fas fa-hourglass-end"></i>
                </span>
                <div className="info-box-content">
                  <span className="info-box-text">DLC dépassée</span>
                  <span className="info-box-number">{expiredCount}</span>
                </div>
              </div>
            </div>
            <div className="col-md-4 col-sm-12">
              <div className="info-box" style={cardStyle}>
                <span className="info-box-icon bg-secondary">
                  <i className="fas fa-archive"></i>
                </span>
                <div className="info-box-content">
                  <span className="info-box-text">Lots archivés</span>
                  <span className="info-box-number">{archivedLot.length}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="card card-outline card-dark" style={cardStyle}>
            <div className="card-header">
              <h3 className="card-title">
                <i className="fas fa-filter mr-2"></i>Recherche
              </h3>
            </div>
            <div className="card-body">
              <div className="row">
                <div className="col-md-6">
                  <div className="form-group mb-md-0">
                    <label htmlFor="lotSearch" style={labelStyle}>
                      Numéro de série
                    </label>
                    <div className="input-group">
                      <div className="input-group-prepend">
                        <span className="input-group-text">
                          <i className="fas fa-search"></i>
                        </span>
                      </div>
                      <input
                        id="lotSearch"
                        type="text"
                        className="form-control"
                        placeholder="Saisir un numéro de série"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                      />
                      {search && (
                        <div className="input-group-append">
                          <button
                            type="button"
                            className="btn btn-outline-secondary"
                            title="Effacer"
                            onClick={() => setSearch("")}
                          >
                            <i className="fas fa-times"></i>
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="form-group mb-0">
                    <label style={labelStyle}>État du lot</label>
                    <div className="btn-group d-flex" role="group">
                      <button
                        type="button"
                        className={statusBtnClass("dispo")}
                        style={{ flex: 1 }}
                        onClick={() => setStatus("dispo")}
                      >
                        <i className="fas fa-check-circle mr-1"></i>
                        Disponibles ({availableLot.length})
                      </button>
                      <button
                        type="button"
                        className={statusBtnClass("archived")}
                        style={{ flex: 1 }}
                        onClick={() => setStatus("archived")}
                      >
                        <i className="fas fa-archive mr-1"></i>
                        Non disponibles ({archivedLot.length})
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="card" style={cardStyle}>
            <div className="card-header">
              <h3 className="card-title">
                {isArchivedTab ? "Lots non disponibles" : "Lots disponibles"}
                <span className="badge badge-dark ml-2">{filteredLots.length}</span>
              </h3>
            </div>
            <div className="card-body table-responsive p-0">
              <table className="table table-hover text-nowrap mb-0">
                <thead>
                  <tr>
                    <th>Numéro de série</th>
                    <th>DLC</th>
                    <th>DLE</th>
                    <th className="text-right" style={{ width: 120 }}>
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="4" className="text-center text-muted py-4">
                        <span className="spinner-border spinner-border-sm mr-2"></span>
                        Chargement…
                      </td>
                    </tr>
                  ) : pageLots.length === 0 ? (
                    <tr>
                      <td colSpan="4" className="text-center text-muted py-4">
                        <i className="fas fa-box-open fa-2x d-block mb-2"></i>
                        {search ? "Aucun lot ne correspond à la recherche" : "Aucun lot"}
                      </td>
                    </tr>
                  ) : (
                    pageLots.map((element) => (
                      <tr key={element.id}>
                        <td>
                          <strong>{element.serialNumber}</strong>
                        </td>
                        <td>
                          <ExpiryDate value={element.dlc} />
                        </td>
                        <td>
                          <ExpiryDate value={element.dle} />
                        </td>
                        <td className="text-right">
                          {isArchivedTab ? (
                            <button
                              className="btn btn-sm btn-outline-info"
                              title="Restaurer"
                              onClick={() => onArchiveHandler(element.id, 0)}
                            >
                              <i className="fas fa-trash-restore mr-1"></i>
                              Restaurer
                            </button>
                          ) : (
                            <button
                              className="btn btn-sm btn-outline-danger"
                              title="Archiver"
                              onClick={() => onArchiveHandler(element.id, 1)}
                            >
                              <i className="fas fa-archive mr-1"></i>
                              Archiver
                            </button>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            {pageCount > 1 && (
              <div className="card-footer d-flex justify-content-between align-items-center">
                <small className="text-muted">
                  Page {page + 1} / {pageCount}
                </small>
                <div>
                  <button
                    className="btn btn-sm btn-outline-dark mr-2"
                    disabled={page === 0}
                    onClick={() => setPage(page - 1)}
                  >
                    <i className="fas fa-chevron-left"></i>
                  </button>
                  <button
                    className="btn btn-sm btn-outline-dark"
                    disabled={page >= pageCount - 1}
                    onClick={() => setPage(page + 1)}
                  >
                    <i className="fas fa-chevron-right"></i>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <Snackbar
        open={alert.open}
        autoHideDuration={2000}
        onClose={closeAlert}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Alert variant="filled" onClose={closeAlert} severity={alert.severity}>
          {alert.text}
        </Alert>
      </Snackbar>
    </div>
  );
};
export default ManufacturingBatch;
